<template>
	<b-container fluid class="admin-project-code-page">
		<b-row class="">
			<b-col cols="12" lg="12">
                <admin-page-header title="프로젝트 관리" description="Assessment에서 사용되는 프로젝트를 관리합니다."/>
			</b-col>
		</b-row>
		<template v-if="!is_loaded_projectCode" >
			<b-row class="admin-body-full-height mt-3">
				<b-col cols="12">
					<div class="d-flex flex-column align-items-center justify-content-center bgray-200 h-100 rounded">
						<b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem;"></b-spinner>
						<h6 class="mb-0 mt-3 text-secondary">프로젝트를 불러오는 중입니다...</h6>
					</div>
				</b-col>
			</b-row>
		</template>
		<template v-else>
			<b-row class="mt-4">
				<b-col cols="12">
					<b-card no-body class="shadow-sm project-list-card" header-bg-variant="white">
                        <template #header>
                            <div class="d-flex align-items-center justify-content-between flex-wrap">
                                <div class="d-flex align-items-center">
                                    <h5 class="mb-0">프로젝트 리스트</h5>
                                    <b-badge v-if="currentPeriodProjects.length" pill variant="primary" class="ml-2 f-85">
                                        {{ currentPeriodProjects.length }}건
                                    </b-badge>
                                </div>
                                <b-btn
                                    size="sm"
                                    variant="primary"
                                    :disabled="!is_loaded_projectCode || !!load_error"
                                    @click="openProjectModal"
                                    >
                                    <fa icon="plus" class="mr-2"></fa>프로젝트 추가하기
                                </b-btn>
                            </div>
                        </template>
						<page-empty-component v-if="load_error"
							compact
							:error="load_error"
							@retry="getProjecCode"
							/>
						<!-- 기간 구성이 바뀌면(새 기간 저장 등) 탭을 새로 그려 선택 index 가 어긋나지 않게 한다 -->
						<b-tabs v-else
							:key="periodKeys"
							:value="currentPeriodIndex"
							card
							@input="onTabInput"
							>
							<b-tab
								v-for="(item) in projects" :key="item.period"
                                title-link-class="gray-500"
                                no-body
								>
								<template #title>
									<span class="d-inline-flex align-items-center">
										{{ item.period }}
										<b-badge pill variant="light" class="ml-2 period-count-badge">{{ item.projects.length }}</b-badge>
									</span>
								</template>
								<!-- DS 표 규칙: 행 44 고정 · 칸마다 한 줄(넘치면 줄이고 전체는 title). 폭은 머리가 정한다 -->
								<b-table
									id="admin-payment-product-table"
									class="border-bottom border-color-gray-300 mb-0 sticky-header-sort-fix"
									table-class="ds-table ds-table-fixed project-code-table"

									:items="item.projects"
									:fields="project_fields"
									:busy="!is_loaded_projectCode"

									empty-html="<h6 class='mt-3 gray-400'>조회된 프로젝트코드가 없습니다.</h6>"
									details-td-class="bg-light"
									show-empty
									hover
									responsive="sm"
									sticky-header="500px"
									no-border-collapse
									>

									<!-- 번호는 탭 안 표시 순서 -->
									<template #cell(index)="data">
										{{ data.index + 1 }}
									</template>

									<!-- 메인 칸 = 수정 창(상세 화면이 따로 없어 수정 창이 상세 역할) -->
									<template #cell(project_code)="data">
										<button type="button" class="ds-detail-link ds-ellipsis"
											:title="`${data.item.project_code} 수정`"
											@click="openProjectEditModal(data.item)">{{ data.item.project_code }}</button>
									</template>

									<template #cell(campaign)="data">
										<span v-if="data.item.campaign" class="ds-ellipsis" :title="`${data.item.campaign.name} (${data.item.campaign.id})`">
											{{ data.item.campaign.name }} <span class="ds-sub">{{ data.item.campaign.id }}</span>
										</span>
										<span v-else class="ds-sub">-</span>
									</template>

									<!--
										평가 그룹 미지정이면 이 프로젝트의 벤더코드가 평가 신청 화면에
										노출되지 않으므로, 대시(-)로 흘리지 않고 경고로 드러낸다.
									-->
									<template #cell(group_name)="data">
										<span v-if="data.item.group_name" class="ds-ellipsis" :title="[data.item.group_name, data.item.group_name_en].filter(Boolean).join(' · ')">
											{{ data.item.group_name }} <span class="ds-sub">{{ data.item.group_name_en }}</span>
										</span>
										<span v-else class="ds-status ds-status-danger">미지정</span>
									</template>

									<template #cell(client)="data">
										<span class="ds-ellipsis" :title="[data.value, data.item.manager].filter(Boolean).join(' · ')">
											{{ data.value || '-' }} <span class="ds-sub">{{ data.item.manager }}</span>
										</span>
									</template>

									<template #cell(client_id)="data">
										<span v-if="data.item.client_id" class="ds-ellipsis" :title="`${data.item.company_name || '-'} (${data.item.client_id})`">
											{{ data.item.company_name }} <span class="ds-sub">{{ data.item.client_id }}</span>
										</span>
										<span v-else class="ds-sub">-</span>
									</template>

									<template #cell(custom_template_code)="data">
										<code v-if="data.value" class="ds-ellipsis" :title="data.value">{{ data.value }}</code>
										<span v-else class="ds-sub">-</span>
									</template>

									<template #cell(availability)="data">
										<span class="ds-status" :class="data.value ? 'ds-status-success' : 'ds-status-light'">{{ data.value ? '사용' : '미사용' }}</span>
									</template>

									<!-- 켜진 옵션만 배지로. 두 값 전부는 title 로 -->
									<template #cell(options)="data">
										<span class="ds-ellipsis" :title="`유료 ${data.item.pay ? 'ON' : 'OFF'} · 업체코드 ${data.item.vendor_code ? 'ON' : 'OFF'}`">
											<span v-if="data.item.pay" class="ds-tag">유료</span> <span v-if="data.item.vendor_code" class="ds-tag">업체코드</span> <span v-if="!data.item.pay && !data.item.vendor_code" class="ds-sub">-</span>
										</span>
									</template>

									<!-- 폭을 아끼려 YY-MM-DD(1차 진행현황과 같은 표기), 전체 날짜는 title -->
									<template #cell(start_date)="data">
										<span :title="`${data.item.start_date || '-'} ~ ${data.item.end_date || '-'}`">{{ formatDate(data.item.start_date, 'YY-MM-DD') || '-' }} ~ {{ formatDate(data.item.end_date, 'YY-MM-DD') || '-' }}</span>
									</template>

									<template #head(edit)="data">
										<span class="sr-only">{{ data.label }}</span>
									</template>

									<template #cell(edit)="data">
										<button type="button" class="ds-row-icon-btn"
											:aria-label="`${data.item.project_code} 수정`" :title="`${data.item.project_code} 수정`"
											@click="openProjectEditModal(data.item)">
											<AppIcon name="pencil"/>
										</button>
									</template>
								</b-table>
							</b-tab>
						</b-tabs>
					</b-card>
				</b-col>
			</b-row>

			<b-modal id="add-new-project-modal"
				class="modal-content"
				centered
				size="lg"
				title="프로젝트 수정"
				hide-footer
				no-close-on-backdrop
				@hide="onProjectModalHide"
				>
				<edit-project
					ref="editProject"
					:edit="edit === 'update'"
					:project="editing_target_project"
					:project_period="currentPeriod"
					@update-project="updateProject"
					@delete-project="deleteProject"
					/>
			</b-modal>
		</template>
	</b-container>
</template>

<script>
import editProject from '@shared_components/admin/components/projects/edit-project'
import PageEmptyComponent from '@shared_components/admin/components/common/PageEmptyComponent.vue'

export default {
	name: 'AdminProjectCode',
	props: [],
	components: {
		editProject,
		PageEmptyComponent
	},
	data () {
		return {
			projects:[],
			editing_target_project: {},

			edit: null,
			currentPeriodIndex: null,
			is_loaded_projectCode: false,
			load_error: '',
			// 배정 콘솔 등에서 ?project_code= 로 넘어오면 목록을 받은 뒤 그 프로젝트 수정 창을 연다(일회성이라 URL 에서는 지운다)
			linked_code: this.$route.query.project_code || '',

			// DS 표 규칙(한 줄 머리 · 번호 → 메인 → 데이터 → 행 액션은 맨 끝). 폭을 안 적은 캠페인 · 평가 그룹 · 원청사 · 대시보드가 남는 폭을 나눠 갖는다.
			// 유료 · 업체코드는 옵션 칸에, 종료일은 기간 칸에 함께 보인다(정렬은 기간 = 시작일, 옵션은 정렬 없음).
			project_fields: [
				{ key: 'index', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no', thStyle: { width: '56px' } },
				{ key: 'project_code', label: '프로젝트코드', sortable: true, thStyle: { width: '150px' } },
				// 캠페인은 객체라 이름으로 정렬한다
				{ key: 'campaign', label: '캠페인', sortable: true, formatter: campaign => (campaign ? campaign.name : ''), sortByFormatted: true },
				{ key: 'group_name', label: '평가 그룹', sortable: true },
				{ key: 'client', label: '원청사', sortable: true },
				{ key: 'client_id', label: '대시보드', sortable: true },
				{ key: 'custom_template_code', label: '커스텀 질문지', sortable: true, thStyle: { width: '120px' } },
				{ key: 'availability', label: '현재사용', sortable: true, thStyle: { width: '88px' } },
				{ key: 'options', label: '옵션', thStyle: { width: '136px' } },
				{ key: 'start_date', label: '기간', sortable: true, tdClass: 'ds-cell-nowrap', thStyle: { width: '160px' } },
				{ key: 'edit', label: '수정', tdClass: 'text-right', thStyle: { width: '60px' } },
			]
		}
	},
	computed: {
		currentPeriodProjects () {
			const period = this.projects[this.currentPeriodIndex]
			return period && Array.isArray(period.projects) ? period.projects : []
		},
		currentPeriod () {
			const group = this.projects[this.currentPeriodIndex]
			return group ? group.period : ''
		},
		// 기본 탭은 마지막(가장 최근 기간) — 목록이 기간 오름차순으로 온다
		defaultPeriodIndex () {
			return this.projects.length - 1
		},
		periodKeys () {
			return this.projects.map(group => group.period).join('|')
		}
	},
	watch: {
		// 뒤로가기 등으로 ?period 가 바뀌면 탭을 맞춘다
		'$route.query.period' (period) {
			if (this.projects.length) this.selectPeriodTab(period, 'replace')
		}
	},
	async created () {
		await this.getProjecCode()
	},
	// 수정 창에 저장하지 않은 입력이 있으면 다른 화면으로 나가기 전에 묻는다(자식 편집기의 가드는 라우터가 부르지 않는다)
	beforeRouteLeave (to, from, next) {
		const editor = this.$refs.editProject
		if (!editor) return next()
		editor.confirmDiscardChanges().then((ok) => next(ok ? undefined : false))
	},
	methods: {
		// silent: 저장·삭제 뒤 다시 불러올 때 전체 로딩 화면을 띄우지 않는다. period: 다시 불러온 뒤 열 기간 탭
		async getProjecCode({ silent = false, period = this.$route.query.period } = {}){
			try {
				if (!silent) this.is_loaded_projectCode = false
				this.load_error = ''
				const response = await this.$axios.get('/project_code')
				this.projects = response.data.reduce((_projectGroup, _projectByPeriod) => {
					if (_projectByPeriod.projects.length) {
						_projectByPeriod.projects = _projectByPeriod.projects.reduce((_projects, _project) => {
							_projects.push({ ..._project,
								start_date: this.formatDate(_project.start_date, 'YYYY-MM-DD'),
								end_date: this.formatDate(_project.end_date, 'YYYY-MM-DD')
							})
							return _projects
						}, [])
						// 최신순(id 내림차순) 정렬 — id가 큰(최근 생성된) 프로젝트가 먼저 오도록 한다.
						_projectByPeriod.projects.sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0))
					}

					_projectGroup.push(_projectByPeriod)
					return _projectGroup
				}, [])
				this.selectPeriodTab(period, 'replace')
				if (this.linked_code) {
					const code = this.linked_code
					this.linked_code = ''
					// 수정 창은 로딩이 끝나야 그려진다(v-else) — 로딩을 풀고 다음 렌더에서 연다
					this.is_loaded_projectCode = true
					this.$nextTick(() => this.openLinkedProject(code))
				}
			} catch (error) {
				console.error(error)
				this.load_error = '프로젝트 목록을 불러오지 못했습니다.'
			} finally {
				this.is_loaded_projectCode = true
			}
		},
		// 기간 탭을 고르고 URL 을 맞춘다. 없는 기간이면 기본 탭(최신)으로 — 잘못된 ?period 는 지워진다
		selectPeriodTab (period, method) {
			const index = this.projects.findIndex(group => group.period === period)
			this.currentPeriodIndex = index > -1 ? index : this.defaultPeriodIndex
			this.syncPeriodQuery(method)
		},
		// 기본 탭이면 키를 생략한다. 이 화면의 query 는 period 하나뿐이라 project_code 등은 여기서 지워진다
		syncPeriodQuery (method) {
			const query = this.currentPeriodIndex === this.defaultPeriodIndex ? {} : { period: this.currentPeriod }
			if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return
			this.$router[method]({ query }).catch(() => {})
		},
		// 사용자가 탭을 누름. 프로그램이 탭을 맞출 때도 b-tabs 가 input 을 내지만 그때는 URL 이 이미 같아 무시된다
		onTabInput (index) {
			if (index < 0) return
			this.currentPeriodIndex = index
			this.syncPeriodQuery('push')
		},
		// 링크의 기간 탭에서 먼저 찾고, 없으면 다른 기간에서 찾는다
		openLinkedProject (code) {
			const group = [this.projects[this.currentPeriodIndex], ...this.projects]
				.find(_group => _group && _group.projects.some(p => p.project_code === code))
			if (!group) {
				this.$EventBus.$emit('toast-failure', '프로젝트 관리', `링크의 프로젝트 코드(${code})를 찾을 수 없습니다.`)
				return
			}
			this.selectPeriodTab(group.period, 'replace')
			this.openProjectEditModal(group.projects.find(p => p.project_code === code))
		},
		openProjectModal() {
			this.edit = 'create'
			// 보고 있는 기간 탭으로 PERIOD 를 미리 채운다(그 기간의 캠페인을 고를 수 있게)
			this.editing_target_project = this.currentPeriod ? { period: this.currentPeriod } : {}

			this.$bvModal.show('add-new-project-modal')
		},
		openProjectEditModal (item) {
			this.edit = 'update'
			this.editing_target_project = { ...item }

			this.$bvModal.show('add-new-project-modal')
		},
		// 목록을 손으로 고치지 않고 다시 불러온다 — 기간이 바뀌었을 수 있고 날짜 형식·정렬도 조회에서만 맞춰진다
		async updateProject (_saved_project) {
			this.$bvModal.hide('add-new-project-modal')
			await this.getProjecCode({ silent: true, period: (_saved_project && _saved_project.period) || this.currentPeriod })
		},
		async deleteProject () {
			this.$bvModal.hide('add-new-project-modal')
			await this.getProjecCode({ silent: true, period: this.currentPeriod })
		},
		// 바깥 클릭은 막아 두고, ESC·X 로 닫을 때 저장하지 않은 입력이 있으면 묻는다. 저장·삭제 중에는 닫지 않는다
		async onProjectModalHide (bvEvent) {
			const editor = this.$refs.editProject
			if (!editor || !['backdrop', 'esc', 'headerclose'].includes(bvEvent.trigger)) return
			if (editor.isSavingProject || editor.isDeletingProject) {
				bvEvent.preventDefault()
				return
			}
			if (!editor.hasUnsavedChanges) return
			bvEvent.preventDefault()
			if (await editor.confirmDiscardChanges({ title: '저장하지 않은 입력', message: '입력한 내용이 저장되지 않았습니다. 닫을까요?' })) {
				this.$bvModal.hide('add-new-project-modal')
			}
		}
	}
}
</script>

<style lang="scss">
.admin-project-code-page {
    .project-list-card {
        border: 1px solid #edf0f2;
        border-radius: 12px;
        overflow: hidden;

        .card-header {
            border-bottom: 1px solid #edf0f2;
            padding-top: 1rem;
            padding-bottom: 1rem;
        }
    }

    // period 탭 제목 옆 개수 배지
    .period-count-badge {
        font-weight: 600;
        color: #6c757d;
        background-color: #f1f3f5;
    }

    // 활성 탭의 개수 배지는 primary 톤으로 강조
    .nav-tabs .nav-link.active .period-count-badge {
        color: #0d6efd;
        background-color: rgba(13, 110, 253, 0.12);
    }

    // 고정 열 합(No. 56 · 코드 150 · 질문지 120 · 사용 88 · 옵션 136 · 기간 160 · 수정 60 = 770) + 줄어드는 4칸(각 약 92).
    // 1440(메뉴 펼침) 본문 폭 약 1148 안에 들어가게 — 그보다 좁으면 가로 스크롤(1366 펼침은 이전 auto 표도 넘쳤다)
    .project-code-table {
        min-width: 1140px;
    }

    .f-85 {
        font-size: 0.85rem;
    }
}
</style>